import { useRef, useState } from 'react';
import { Button, Modal, Badge, useToast } from '../../../shared/components';
import { getTurnoGeoRepository } from '../../../shared/services/turnoGeo/repositoryFactory';
import { ImportMarkResult, ImportMarkRow } from '../../../shared/services/turnoGeo/types';
import { parseMarksFile, downloadMarksTemplate } from '../hoursExcel';

const repo = getTurnoGeoRepository();
const CHUNK = 100;

/** Carga masiva de marcaciones históricas (p. ej. los meses de 2025 para las pruebas). Solo administrador. */
export function ImportMarksModal({ open, onClose, onDone }: { open: boolean; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const [rows, setRows] = useState<ImportMarkRow[]>([]);
  const [fileName, setFileName] = useState('');
  const [results, setResults] = useState<ImportMarkResult[]>([]);
  const [progress, setProgress] = useState<string | null>(null);
  const [running, setRunning] = useState(false);

  const reset = () => { setRows([]); setFileName(''); setResults([]); setProgress(null); if (fileRef.current) fileRef.current.value = ''; };
  const close = () => { reset(); onClose(); };

  const pick = async (f: File | undefined) => {
    if (!f) return;
    try { const r = await parseMarksFile(f); setRows(r); setFileName(f.name); setResults([]); }
    catch (e) { toast.show(e instanceof Error ? e.message : 'No se pudo leer el archivo.', 'error'); reset(); }
  };

  const run = async () => {
    setRunning(true);
    const all: ImportMarkResult[] = [];
    try {
      for (let i = 0; i < rows.length; i += CHUNK) {
        setProgress(`Cargando ${Math.min(i + CHUNK, rows.length)} de ${rows.length}…`);
        const part = await repo.importMarks(rows.slice(i, i + CHUNK));
        all.push(...part.map((r) => ({ ...r, row: r.row + i })));
        setResults([...all]);
      }
      toast.show('Carga terminada.');
      onDone();
    } catch (e) { toast.show(e instanceof Error ? e.message : 'La carga se interrumpió.', 'error'); }
    finally { setRunning(false); setProgress(null); }
  };

  const count = (s: ImportMarkResult['status']) => results.filter((r) => r.status === s).length;

  return (
    <Modal open={open} onClose={close} title="Cargar marcaciones (Excel)">
      <div className="space-y-3">
        <p className="text-sm text-ink-600">Sube el histórico (por ejemplo los meses de 2025) para probar el cálculo. Cada fila es un día: <b>EMAIL o CC, FECHA, ENTRADA, SALIDA ALMUERZO, ENTRADA ALMUERZO, SALIDA</b>. Las marcas quedan como modalidad Oficina y con la nota «Cargado desde Excel». Si ya existen, se omiten (no se duplican).</p>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={downloadMarksTemplate}>Descargar plantilla</Button>
          <Button variant="outline" onClick={() => fileRef.current?.click()}>Elegir archivo</Button>
          <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={(e) => pick(e.target.files?.[0])} />
        </div>
        {fileName && <p className="text-sm text-ink-700">{fileName}: <b>{rows.length}</b> filas listas.</p>}
        {rows.length > 0 && results.length === 0 && <Button fullWidth onClick={run} isLoading={running}>{progress ?? `Cargar ${rows.length} filas`}</Button>}
        {progress && results.length > 0 && <p className="text-sm text-ink-500">{progress}</p>}
        {results.length > 0 && (
          <div className="space-y-2">
            <div className="flex flex-wrap gap-2">
              <Badge tone="success">{count('IMPORTADA')} importadas</Badge>
              <Badge tone="neutral">{count('DUPLICADA')} duplicadas</Badge>
              <Badge tone={count('ERROR') ? 'critical' : 'neutral'}>{count('ERROR')} con error</Badge>
            </div>
            <div className="max-h-56 overflow-auto rounded-lg border border-ink-100 text-xs">
              {results.filter((r) => r.status !== 'IMPORTADA').map((r) => (
                <div key={r.row} className="flex gap-2 border-b border-ink-50 px-3 py-1.5"><span className="w-12 text-ink-400">Fila {r.row + 1}</span><span className="font-medium">{r.status}</span><span className="text-ink-600">{r.message}</span></div>
              ))}
              {results.every((r) => r.status === 'IMPORTADA') && <p className="p-3 text-ink-500">Todas las filas se importaron sin novedad.</p>}
            </div>
          </div>
        )}
        <Button variant="outline" fullWidth onClick={close}>Cerrar</Button>
      </div>
    </Modal>
  );
}
